import { useState } from "react";
import { Alert, Button, Card, Form, Input, Space, Typography, message } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { masteruri } from "../../config";
import { cardmargin, grayheader } from "../CSSReferences";
import { MIN_LENGTH, confirmOutcome, confirmPath, formProblem, keyFromFragment } from "./reset_logic";

const { Paragraph } = Typography;

/** Reads the key from "#t=..." once and takes it out of the address bar. */
function takeKeyFromLocation(): string {
    const key = keyFromFragment(window.location.hash);
    if (window.location.hash) {
        window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    }
    return key;
}

/**
 * Sets a new password with the one-time key from the reset mail (02.11.2026).
 * Opened from the mail link (key in the fragment) or from the login page
 * ("I have a recovery key", key typed in). Opening the page changes nothing;
 * only the form sends the key, with the new password, to resetpw/confirm.
 */
export function PasswortZuruecksetzen() {
    const navigate = useNavigate();
    const [initialKey] = useState<string>(takeKeyFromLocation);
    const [problem, setProblem] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const submit = (values: { key: string; password: string; repeated: string }) => {
        const key = (values.key ?? "").trim();
        const wrong = formProblem(key, values.password ?? "", values.repeated ?? "");
        if (wrong !== null) {
            setProblem(wrong);
            return;
        }
        setBusy(true);
        setProblem(null);
        fetch(confirmPath(masteruri), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ Token: key, Password: values.password }),
        })
            .then(async response => confirmOutcome(response.status, await response.json().catch(() => null)))
            .catch(() => confirmOutcome(0, null))
            .then(outcome => {
                setBusy(false);
                if (outcome.kind === "done") {
                    message.success({ content: "Passwort gesetzt - bitte anmelden. / Password set - please sign in.", duration: 6 });
                    navigate("/login");
                } else {
                    setProblem(outcome.message);
                }
            });
    };

    return (
        <Card title="Passwort zurücksetzen / Reset password" style={cardmargin} styles={{ header: grayheader }}>
            <Space orientation="vertical" size={16} style={{ width: "100%" }}>
                <Paragraph style={{ marginBottom: 0 }}>
                    Geben Sie den Schlüssel aus der Mail und Ihr neues Passwort ein. Der Schlüssel gilt
                    30 Minuten und nur einmal. / Enter the key from the mail and your new password.
                    The key is valid for 30 minutes and once only.
                </Paragraph>
                {problem !== null && <Alert type="error" showIcon message={problem} />}
                <Form layout="vertical" onFinish={submit} initialValues={{ key: initialKey }} style={{ maxWidth: "32em" }}>
                    <Form.Item label="Schlüssel / Recovery key" name="key"
                               rules={[{ required: true, message: "Bitte den Schlüssel eingeben." }]}>
                        <Input autoComplete="off" spellCheck={false} />
                    </Form.Item>
                    <Form.Item label="Neues Passwort / New password" name="password"
                               rules={[{ required: true, message: "Bitte ein neues Passwort eingeben." },
                                       { min: MIN_LENGTH, message: `Mindestens ${MIN_LENGTH} Zeichen.` }]}>
                        <Input.Password autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item label="Neues Passwort wiederholen / Repeat new password" name="repeated" dependencies={["password"]}
                               rules={[{ required: true, message: "Bitte das neue Passwort wiederholen." },
                                       ({ getFieldValue }) => ({
                                           validator(_rule: unknown, value: string) {
                                               return !value || getFieldValue("password") === value
                                                   ? Promise.resolve()
                                                   : Promise.reject(new Error("Die beiden Eingaben stimmen nicht überein."));
                                           },
                                       })]}>
                        <Input.Password autoComplete="new-password" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={busy}>Passwort setzen / Set password</Button>
                            <Link to="/login">Zur Anmeldung / Back to login</Link>
                        </Space>
                    </Form.Item>
                </Form>
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Länge schlägt Komplexität: Eine Folge mehrerer Wörter ist sicherer und leichter zu merken
                    als ein kurzes Passwort mit Sonderzeichen.
                </Paragraph>
            </Space>
        </Card>
    );
}
